import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { 
  Card, 
  Form, 
  Button, 
  Row, 
  Col, 
  Alert, 
  InputGroup,
  Spinner,
  ToggleButton,
  ToggleButtonGroup,
} from 'react-bootstrap';
import { toast } from 'react-hot-toast';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faHome,
  faMapMarkerAlt,
  faCheckCircle,
  faExclamationTriangle,
  faSearch,
  faUser,
  faFileAlt,
  faShieldAlt,
  faSync,
  faTimes,
  faArrowRight
} from '@fortawesome/free-solid-svg-icons';
import { useWorkflow, PropertyInput, MLSData } from '../WorkflowProvider';
import axiosInstance from 'helpers/api';

// Debug utilities
const DEBUG_PREFIX = '🔍 [STEP1-STREAMLINED]';
const debugLog = (stage: string, message: string, data?: any) => {
  const timestamp = new Date().toISOString();
  console.log(`${DEBUG_PREFIX} [${timestamp}] [${stage}] ${message}`);
  if (data) console.log(`${DEBUG_PREFIX} [${timestamp}] [${stage}] Data:`, data);
};

// Interfaces
interface AddressSuggestion {
  address: string;
  coordinates: [number, number] | null;
  confidence: number;
  source: string;
}

interface CustomerOption {
  id: string;
  label: string;
  first_name: string;
  last_name: string;
  email?: string;
  phone?: string;
  lead_type?: string;
}

interface Step1StreamlinedProps {
  onComplete: (data: PropertyInput) => void;
}

const Step1PropertyInputStreamlined: React.FC<Step1StreamlinedProps> = ({ onComplete }) => {
  const { state, setPropertyInput, setMLSData: setWorkflowMLSData } = useWorkflow();
  const { register, handleSubmit, formState: { errors }, setValue, getValues, watch } = useForm<PropertyInput>();

  // Core state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [, setLookupStatus] = useState<'none' | 'loading' | 'success' | 'error'>('none');

  // Address handling
  const [addressValue, setAddressValue] = useState('');
  const [addressSuggestions, setAddressSuggestions] = useState<AddressSuggestion[]>([]);
  const [isLoadingSuggestions, setIsLoadingSuggestions] = useState(false);
  const [addressLocked, setAddressLocked] = useState(false);
  
  // Property confirmation
  const [mlsData, setLocalMLSData] = useState<MLSData | null>(null);
  const [propertyConfirmed, setPropertyConfirmed] = useState(false);
  
  // Report details
  const [reportName, setReportName] = useState('');
  
  // Customer selection
  const [selectedCustomer, setSelectedCustomer] = useState<CustomerOption | null>(null);
  const [customerOptions, setCustomerOptions] = useState<CustomerOption[]>([]);
  const [customerSearchTerm, setCustomerSearchTerm] = useState('');
  const [isLoadingCustomers, setIsLoadingCustomers] = useState(false);
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
  
  // Transaction type state (default: sale)
  const [transactionType, setTransactionType] = useState<'sale' | 'rental'>('sale');

  // Initialize form with existing data
  useEffect(() => {
    if (state.propertyInput) {
      const { property_address, customerName } = state.propertyInput;
      if (property_address) {
        setAddressValue(property_address);
        setValue("property_address", property_address);
      }
      if (customerName) setValue("customerName", customerName);
    }
    if (state.mlsData) {
      setLocalMLSData(state.mlsData);
      setPropertyConfirmed(true);
      setAddressLocked(true);
    }
  }, [state, setValue]);

  // Address suggestions
  const fetchAddressSuggestions = async (query: string): Promise<AddressSuggestion[]> => {
    if (query.length < 3) return [];
    
    try {
      setIsLoadingSuggestions(true);
      const response = await axiosInstance.get(`/properties/address-suggestions-public?query=${encodeURIComponent(query)}`);
      
      if (response.data.success && response.data.suggestions) {
        return response.data.suggestions;
      }
      return [];
    } catch (error) {
      console.error('Address suggestions failed:', error);
      return [];
    } finally {
      setIsLoadingSuggestions(false);
    }
  };

  // Handle address input changes
  const handleAddressChange = async (value: string) => {
    setAddressValue(value);
    setValue("property_address", value);
    
    if (value.length >= 3 && !addressLocked) {
      const suggestions = await fetchAddressSuggestions(value);
      setAddressSuggestions(suggestions);
    } else {
      setAddressSuggestions([]);
    }
  };

  // Handle address selection
  const handleAddressSelect = (suggestion: AddressSuggestion) => {
    setAddressValue(suggestion.address);
    setValue("property_address", suggestion.address);
    setAddressSuggestions([]);
    debugLog('ADDRESS_SELECT', 'Address selected from suggestions', suggestion);
  };

  // Property lookup - Updated to use seller-optimized endpoint
  const handlePropertyLookup = async () => {
    if (!addressValue || addressValue.length < 10) {
      setError('Please enter a complete address');
      return;
    }

    setLoading(true);
    setLookupStatus('loading');
    setError(null);
    
    try {
      debugLog('PROPERTY_LOOKUP', 'Starting seller-optimized CMA analysis', { address: addressValue });
      
      // STEP 1A: Use enhanced smart CMA endpoint for full analysis
      const response = await axiosInstance.post('/properties/smart-cma-enhanced', {
        subject_property: {
          address: addressValue,
          bedrooms: parseInt(getValues('bedrooms')) || 3,
          bathrooms: parseFloat(getValues('bathrooms')) || 2,
          living_area: parseInt(getValues('living_area')) || 1500,
          year_built: parseInt(getValues('year_built')) || 2000,
          city: '',  // Will be extracted from address
          postal_code: ''  // Will be extracted from address
        },
        options: {
          max_distance_miles: 2.0,
          months_back: 12,
          include_xgboost_analysis: true,
          analysis_type: 'seller_comprehensive',
          transaction_type: transactionType  // Add transaction type to options
        },
        lead_id: selectedCustomer?.id || null
      });

      if (response.data.success) {
        // Handle the enhanced smart CMA response
        const { report_id, analysis_results } = response.data;
        // CRITICAL FIX: Data is in analysis_results.comparable_properties.comparables, not analysis_results.comparables
        const comparablesData = analysis_results?.comparable_properties?.comparables || analysis_results?.comparables || [];
        const subjectProperty = analysis_results?.subject_property || {};
        const totalFound = analysis_results?.comparable_properties?.total_found || analysis_results?.total_found || 0;
        
        debugLog('PROPERTY_LOOKUP', 'Enhanced analysis complete', {
          reportId: report_id,
          comparablesFound: totalFound,
          xgboostValue: analysis_results?.market_value_estimate?.estimated_value
        });
        
        if (report_id) {
          // STEP 1B: Store enhanced smart CMA data
          const mlsFormatData = {
            address: addressValue,
            bedrooms: subjectProperty.bedrooms || parseInt(getValues('bedrooms')) || 3,
            bathrooms: subjectProperty.bathrooms || parseFloat(getValues('bathrooms')) || 2,
            living_area: subjectProperty.living_area || parseInt(getValues('living_area')) || 1500,
            year_built: subjectProperty.year_built || parseInt(getValues('year_built')) || 2000,
            lot_size: subjectProperty.lot_size || 7000,
            property_type: subjectProperty.property_type || 'Single Family',
            estimated_value: analysis_results?.market_value_estimate?.estimated_value || 0,
            confidence_score: analysis_results?.market_value_estimate?.confidence || 0.85,
            report_id: report_id
          };
          
          // Auto-fill form with property data if available
          if (subjectProperty.bedrooms) setValue('bedrooms', String(subjectProperty.bedrooms));
          if (subjectProperty.bathrooms) setValue('bathrooms', String(subjectProperty.bathrooms));
          if (subjectProperty.living_area) setValue('living_area', String(subjectProperty.living_area));
          if (subjectProperty.year_built) setValue('year_built', String(subjectProperty.year_built));
          
          setLocalMLSData(mlsFormatData);
          setPropertyConfirmed(true);
          setAddressLocked(true);
          setLookupStatus('success');

          // FIXED: Only store minimal data to avoid QuotaExceededError
          // The full data is already saved in the database, Step 2 will fetch it by report_id
          try {
            // Only store essential IDs and minimal property info
            sessionStorage.setItem('currentReportId', report_id);

            // Store minimal data for Step 2 - just subject property summary and count
            const minimalDataForStep2 = {
              success: true,  // IMPORTANT: Form validation checks for this
              report_id: report_id,
              subject_property: mlsFormatData,
              comparable_count: totalFound,
              market_value: analysis_results?.market_value_estimate?.estimated_value,
              // Step 2 will fetch full comparables from backend
              fetch_from_backend: true
            };
            sessionStorage.setItem('fullCMAResponse', JSON.stringify(minimalDataForStep2));

            console.log('✅ [STORAGE] Minimal data stored, Step 2 will fetch full data from backend');
          } catch (storageError) {
            // If even minimal storage fails, just store the report_id
            console.warn('⚠️ [STORAGE] sessionStorage limited, storing only report_id:', storageError);
            try {
              sessionStorage.clear(); // Clear old data
              sessionStorage.setItem('currentReportId', report_id);
            } catch (e) {
              console.error('❌ [STORAGE] Critical: Cannot store report_id', e);
            }
          }
          
          // Update workflow state
          setWorkflowMLSData(mlsFormatData);
          setPropertyInput({
            ...getValues(),
            property_address: addressValue
          });
          
          debugLog('PROPERTY_LOOKUP', 'Enhanced CMA analysis completed successfully', {
            propertyData: mlsFormatData,
            comparablesFound: comparablesData.length || 0,
            marketValue: analysis_results?.market_value_estimate?.estimated_value
          });
          
          console.log('🔥 [ENHANCED CMA] Analysis complete:', {
            address: addressValue,
            comparables: totalFound,
            estimatedValue: analysis_results?.market_value_estimate?.estimated_value,
            confidence: Math.round((analysis_results?.market_value_estimate?.confidence || 0.85) * 100) + '%',
            dataSource: 'Enhanced Smart CMA with XGBoost',
            reportId: report_id
          });
          
          // Show success notification
          toast.success(
            <div>
              <strong>Report Created Successfully!</strong>
              <br />
              <span style={{ fontSize: '0.9em' }}>
                Report ID: {report_id.slice(0, 8)}...
                <br />
                Analyzing comparable properties...
              </span>
            </div>,
            {
              duration: 5000,
              icon: '✅',
              style: {
                borderRadius: '10px',
                background: '#10b981',
                color: '#fff',
              },
            }
          );
          
          // Trigger background comp search (don't wait for it)
          console.log('🔄 [BACKGROUND] Triggering comp search for report:', report_id);
          axiosInstance.post(`/properties/smart-cma-enhanced/trigger-comp-search/${report_id}`)
            .then(() => {
              console.log('✅ [BACKGROUND] Comp search triggered successfully');
            })
            .catch(error => {
              console.error('❌ [BACKGROUND] Failed to trigger comp search:', error);
            });
          
        } else {
          setLookupStatus('error');
          setError('Property analysis failed. Please verify the address and try again.');
        }
      } else {
        setLookupStatus('error');
        setError(response.data.error || 'Property analysis failed. Please verify the address and try again.');
        debugLog('PROPERTY_LOOKUP', 'Seller-optimized analysis failed', response.data);
      }
    } catch (error: any) {
      console.error('Seller-optimized CMA error:', error);
      setLookupStatus('error');
      
      // More specific error handling
      if (error.response?.status === 401) {
        setError('Please log in to use the Smart CMA tool.');
        // Optionally redirect to login
        setTimeout(() => {
          window.location.href = '/login?redirect=' + encodeURIComponent(window.location.pathname);
        }, 2000);
      } else if (error.response?.status === 404) {
        setError('CMA service temporarily unavailable. Please try again in a moment.');
      } else if (error.response?.status === 400) {
        setError(error.response.data?.error || 'Invalid property address. Please check and try again.');
      } else if (error.response?.status === 500) {
        // Check if it's ATTOM API auth issue
        const errorMsg = error.response.data?.error || '';
        if (errorMsg.includes('401') || errorMsg.includes('authentication')) {
          setError('Authentication error. Please ensure you are logged in.');
        } else {
          setError('Server error. Please try again in a moment.');
        }
      } else {
        setError('Unable to perform CMA analysis. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  // Customer search
  const fetchCustomers = async (searchTerm: string = '') => {
    setIsLoadingCustomers(true);
    try {
      const response = await axiosInstance.get('/leads', {
        params: searchTerm ? { q: searchTerm, limit: 20 } : { limit: 20 }
      });

      if (response.data.success && response.data.data) {
        const customers = response.data.data.map((lead: any) => ({
          id: lead.id,
          label: `${lead.first_name} ${lead.last_name}${lead.email ? ` - ${lead.email}` : ''}`,
          first_name: lead.first_name,
          last_name: lead.last_name,
          email: lead.email,
          phone: lead.phone,
          lead_type: lead.lead_type
        }));
        setCustomerOptions(customers);
      }
    } catch (error) {
      console.error('Customer search failed:', error);
    } finally {
      setIsLoadingCustomers(false);
    }
  };

  // Handle customer search
  const handleCustomerSearch = (searchTerm: string) => {
    setCustomerSearchTerm(searchTerm);
    if (searchTerm.length >= 2) {
      fetchCustomers(searchTerm);
      setShowCustomerDropdown(true);
    } else {
      setShowCustomerDropdown(false);
    }
  };

  // Handle customer selection
  const handleCustomerSelect = (customer: CustomerOption) => {
    setSelectedCustomer(customer);
    setCustomerSearchTerm(customer.label);
    setValue("customerName", `${customer.first_name} ${customer.last_name}`);
    setShowCustomerDropdown(false);
  };

  // Form submission - Create report and continue workflow
  const onSubmit = async (data: PropertyInput) => {
    try {
      debugLog('FORM_SUBMIT', 'Starting form submission', data);
      setError(null);
      setLoading(true);
      
      // Check if property has been looked up
      if (!propertyConfirmed) {
        setError('Please click "Find Property" to analyze the property before continuing');
        setLoading(false);
        return;
      }
      
      // Get the report ID from sessionStorage
      const reportId = sessionStorage.getItem('currentReportId');
      const fullCMAData = JSON.parse(sessionStorage.getItem('fullCMAResponse') || '{}');
      
      if (!reportId || !fullCMAData.success) {
        setError('Property analysis data not found. Please search for the property again.');
        setLoading(false);
        return;
      }
      
      // Report already created during property lookup
      debugLog('FORM_SUBMIT', 'Using existing report', { reportId });
      
      // Store updated data for later steps
      sessionStorage.setItem('smartCMAReportData', JSON.stringify({
        reportId,
        propertyData: data,
        analysisData: fullCMAData
      }));
      
      // If this is for seller portal, store special flag
      const urlParams = new URLSearchParams(window.location.search);
      if (urlParams.get('source') === 'seller_portal' || urlParams.get('aid')) {
        sessionStorage.setItem('sellerPortalMode', 'true');
        sessionStorage.setItem('agentId', urlParams.get('aid') || '');
      }
      
      // Update workflow state and proceed
      setPropertyInput({
        ...data,
        property_address: addressValue,
        reportId,
        customerName: selectedCustomer?.label || data.customerName || ''
      });
      
      if (mlsData) {
        setWorkflowMLSData(mlsData);
      }
      
      onComplete({
        ...data,
        property_address: addressValue,
        reportId,
        customerName: selectedCustomer?.label || data.customerName || ''
      });
      
    } catch (error: any) {
      console.error('❌ [FORM_SUBMIT] Error:', error);
      setError(error.message || 'Failed to submit property information');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <style>{`
        @keyframes pulse {
          0% { opacity: 1; }
          50% { opacity: 0.5; }
          100% { opacity: 1; }
        }
        @keyframes aiGlow {
          0%, 100% { box-shadow: 0 0 20px rgba(0, 212, 255, 0.3); }
          50% { box-shadow: 0 0 30px rgba(0, 212, 255, 0.6); }
        }
      `}</style>
      
      <Card 
        className="h-100 border-0"
        style={{
          background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 100%)',
          backdropFilter: 'blur(15px)',
          border: '1px solid rgba(0, 212, 255, 0.2) !important',
          borderRadius: '16px'
        }}
      >
        {/* AI-Forward Header */}
        <Card.Header 
          className="border-bottom-0 py-4"
          style={{ 
            background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.15) 0%, rgba(118, 75, 162, 0.15) 100%)',
            backdropFilter: 'blur(10px)',
            borderRadius: '16px 16px 0 0'
          }}
        >
          <div className="d-flex align-items-center justify-content-between">
            <div className="d-flex align-items-center">
              <div 
                className="rounded-circle d-flex align-items-center justify-content-center me-3"
                style={{ 
                  width: "48px", 
                  height: "48px",
                  background: 'linear-gradient(135deg, #00d4ff 0%, #0ea5e9 100%)',
                  animation: 'aiGlow 3s infinite'
                }}
              >
                <FontAwesomeIcon 
                  icon={faHome} 
                  className="text-white" 
                  size="lg"
                  style={{ filter: 'drop-shadow(0 0 4px rgba(255, 255, 255, 0.5))' }}
                />
              </div>
              <div>
                <h4 style={{ 
                  color: '#1f2937',
                  textShadow: '0 0 10px rgba(0, 212, 255, 0.3)',
                  fontSize: '1.1rem',
                  fontWeight: 600,
                  marginBottom: '4px'
                }}>
                  Smart CMA Generator
                </h4>
                <p className="text-muted mb-0" style={{ fontSize: '14px' }}>
                  AI-powered property analysis & market intelligence
                </p>
              </div>
            </div>
            
            {/* AI Status Indicator */}
            <div style={{
              background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(5, 150, 105, 0.1) 100%)',
              padding: '8px 16px',
              borderRadius: '20px',
              border: '1px solid rgba(16, 185, 129, 0.3)'
            }}>
              <div className="rounded-circle me-2" style={{
                width: '8px', height: '8px', background: '#10b981',
                animation: 'pulse 2s infinite', display: 'inline-block'
              }} />
              <span className="fw-semibold text-success" style={{ 
                fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px'
              }}>
                AI READY
              </span>
            </div>
          </div>
        </Card.Header>

        <Card.Body className="p-4">
          <Form onSubmit={handleSubmit(onSubmit)}>
            {/* Error Alert */}
            {error && (
              <Alert variant="danger" className="mb-4" dismissible onClose={() => setError(null)}>
                <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
                {error}
              </Alert>
            )}

            {/* Report Name Section */}
            <Row className="mb-4">
              <Col>
                <Form.Group>
                  <Form.Label className="form-label-header mb-2 d-flex align-items-center fw-semibold" style={{ color: '#374151' }}>
                    <FontAwesomeIcon icon={faFileAlt} className="me-2 text-primary" />
                    Report Name
                  </Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Enter report name (e.g., 'CMA for 123 Main St')"
                    value={reportName}
                    onChange={(e) => setReportName(e.target.value)}
                    className="border-0"
                    style={{
                      background: 'rgba(255, 255, 255, 0.9)',
                      backdropFilter: 'blur(10px)',
                      border: '1px solid rgba(0, 212, 255, 0.2) !important',
                      borderRadius: '8px'
                    }}
                  />
                </Form.Group>
              </Col>
            </Row>

            {/* Address Input Section */}
            <Row className="mb-4">
              <Col>
                <div 
                  className="p-4 rounded-4 border-0"
                  style={{
                    background: 'linear-gradient(135deg, rgba(0, 212, 255, 0.08) 0%, rgba(14, 165, 233, 0.08) 100%)',
                    border: '1px solid rgba(0, 212, 255, 0.2) !important'
                  }}
                >
                  <Form.Group className="position-relative">
                    <Form.Label className="form-label-header mb-3 d-flex align-items-center fw-bold" style={{ color: '#0ea5e9' }}>
                      <div 
                        className="rounded-circle d-flex align-items-center justify-content-center me-3"
                        style={{ 
                          width: "32px", 
                          height: "32px",
                          background: 'linear-gradient(135deg, #00d4ff 0%, #0ea5e9 100%)',
                          boxShadow: '0 0 15px rgba(0, 212, 255, 0.3)'
                        }}
                      >
                        <FontAwesomeIcon icon={faMapMarkerAlt} className="text-white" size="sm" />
                      </div>
                      Property Address
                    </Form.Label>
                    
                    <InputGroup className="shadow-sm">
                      <Form.Control
                        type="text"
                        placeholder="🏠 Enter complete property address (e.g., 123 Main St, Phoenix, AZ 85001)"
                        value={addressValue}
                        {...register("property_address", {
                          required: "Property address is required",
                          minLength: { value: 10, message: "Please enter a complete address" }
                        })}
                        onChange={(e) => handleAddressChange(e.target.value)}
                        isInvalid={!!errors.property_address}
                        className="form-control-lg border-0 rounded-start-3"
                        disabled={addressLocked}
                        style={{
                          background: 'rgba(255, 255, 255, 0.9)',
                          backdropFilter: 'blur(10px)',
                          border: '1px solid rgba(0, 212, 255, 0.3) !important'
                        }}
                      />
                      <InputGroup.Text 
                        className="border-0 rounded-end-3"
                        style={{
                          background: 'rgba(255, 255, 255, 0.9)',
                          border: '1px solid rgba(0, 212, 255, 0.3) !important',
                          borderLeft: 'none !important'
                        }}
                      >
                        {isLoadingSuggestions ? (
                          <Spinner size="sm" style={{ color: '#00d4ff' }} />
                        ) : addressLocked ? (
                          <FontAwesomeIcon icon={faShieldAlt} className="text-success" />
                        ) : (
                          <FontAwesomeIcon icon={faSearch} className="text-muted" />
                        )}
                      </InputGroup.Text>
                    </InputGroup>
                    
                    {errors.property_address && (
                      <Form.Control.Feedback type="invalid" className="d-block">
                        {errors.property_address.message}
                      </Form.Control.Feedback>
                    )}

                    {/* Address Suggestions */}
                    {addressSuggestions.length > 0 && !addressLocked && (
                      <div 
                        className="position-absolute w-100 mt-1"
                        style={{ zIndex: 1000 }}
                      >
                        <Card className="border-0 shadow-lg">
                          <Card.Body className="p-0">
                            {addressSuggestions.map((suggestion, index) => (
                              <div
                                key={index}
                                className="p-3 border-bottom cursor-pointer"
                                style={{
                                  cursor: 'pointer',
                                  background: 'rgba(255, 255, 255, 0.95)',
                                  backdropFilter: 'blur(10px)'
                                }}
                                onClick={() => handleAddressSelect(suggestion)}
                                onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(0, 212, 255, 0.1)'}
                                onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.95)'}
                              >
                                <div className="d-flex align-items-center">
                                  <FontAwesomeIcon icon={faMapMarkerAlt} className="text-primary me-2" />
                                  <div>
                                    <div className="fw-semibold">{suggestion.address}</div>
                                    <small className="text-muted">Confidence: {Math.round(suggestion.confidence * 100)}%</small>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </Card.Body>
                        </Card>
                      </div>
                    )}

                    {/* Search Button */}
                    {!propertyConfirmed && addressValue.length >= 10 && (
                      <div className="mt-3 text-center">
                        <Button 
                          variant="primary" 
                          onClick={handlePropertyLookup}
                          disabled={loading}
                          size="lg"
                          style={{
                            background: 'linear-gradient(135deg, #00d4ff 0%, #0ea5e9 100%)',
                            border: 'none',
                            borderRadius: '12px',
                            padding: '12px 24px'
                          }}
                        >
                          {loading ? (
                            <>
                              <Spinner size="sm" className="me-2" />
                              Searching Property...
                            </>
                          ) : (
                            <>
                              <FontAwesomeIcon icon={faSearch} className="me-2" />
                              Find Property
                            </>
                          )}
                        </Button>
                      </div>
                    )}

                    {/* Change Address Button */}
                    {addressLocked && (
                      <div className="mt-3 text-center">
                        <Button 
                          variant="outline-secondary" 
                          onClick={() => {
                            setAddressLocked(false);
                            setPropertyConfirmed(false);
                            setLocalMLSData(null);
                            setLookupStatus('none');
                          }}
                          size="sm"
                        >
                          <FontAwesomeIcon icon={faSync} className="me-2" />
                          Search Different Property
                        </Button>
                      </div>
                    )}
                  </Form.Group>
                </div>
              </Col>
            </Row>

            {/* Property Confirmation Card */}
            {propertyConfirmed && mlsData && (
              <Row className="mb-4">
                <Col>
                  <Card 
                    className="border-0 shadow-sm"
                    style={{
                      background: 'linear-gradient(135deg, rgba(34, 197, 94, 0.1) 0%, rgba(21, 128, 61, 0.1) 100%)',
                      border: '1px solid rgba(34, 197, 94, 0.3) !important'
                    }}
                  >
                    <Card.Body className="p-4">
                      <div className="d-flex align-items-center justify-content-between">
                        <div className="d-flex align-items-center">
                          <div 
                            className="rounded-circle d-flex align-items-center justify-content-center me-3"
                            style={{ 
                              width: "40px", 
                              height: "40px",
                              background: '#22c55e',
                              boxShadow: '0 0 15px rgba(34, 197, 94, 0.4)'
                            }}
                          >
                            <FontAwesomeIcon icon={faCheckCircle} className="text-white" />
                          </div>
                          <div>
                            <h6 className="mb-1 fw-semibold text-success">Property Confirmed</h6>
                            <p className="mb-0 text-muted small">{addressValue}</p>
                            {mlsData.bedrooms && mlsData.bathrooms && mlsData.living_area && (
                              <p className="mb-0 text-muted small">
                                {mlsData.bedrooms} bed • {mlsData.bathrooms} bath • {mlsData.living_area.toLocaleString()} sqft
                              </p>
                            )}
                            {/* Data Source Attribution */}
                            <p className="mb-0 text-muted small mt-1" style={{ fontStyle: 'italic' }}>
                              Data provided by ATTOM Data Solutions
                            </p>
                          </div>
                        </div>
                        <div className="text-end">
                          {mlsData.list_price && (
                            <div className="fw-bold text-success h5 mb-0">
                              ${mlsData.list_price.toLocaleString()}
                            </div>
                          )}
                          {mlsData.confidence_score && (
                            <small className="text-muted">
                              Confidence: {Math.round(mlsData.confidence_score * 100)}%
                            </small>
                          )}
                        </div>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            )}

            {/* Transaction Type Toggle - Simplified */}
            <Row className="mb-4">
              <Col>
                <Form.Group>
                  <Form.Label className="fw-semibold mb-2" style={{ color: '#374151' }}>
                    Property Type
                  </Form.Label>
                  <ToggleButtonGroup
                    type="radio"
                    name="transactionType"
                    value={transactionType}
                    onChange={(value) => setTransactionType(value)}
                    className="w-100"
                  >
                    <ToggleButton 
                      id="tb-sale"
                      value="sale"
                      variant="outline-primary"
                      className="w-50"
                    >
                      For Sale
                    </ToggleButton>
                    <ToggleButton 
                      id="tb-rental"
                      value="rental"
                      variant="outline-primary"
                      className="w-50"
                    >
                      For Rent
                    </ToggleButton>
                  </ToggleButtonGroup>
                </Form.Group>
              </Col>
            </Row>

            {/* Customer Selection */}
            <Row className="mb-4">
              <Col>
                <Form.Group className="position-relative">
                  <Form.Label className="form-label-header mb-2 d-flex align-items-center fw-semibold" style={{ color: '#374151' }}>
                    <FontAwesomeIcon icon={faUser} className="me-2 text-primary" />
                    Customer (Optional)
                  </Form.Label>
                  <InputGroup>
                    <Form.Control
                      type="text"
                      placeholder="Search existing customers or leave blank for new customer"
                      value={customerSearchTerm}
                      onChange={(e) => handleCustomerSearch(e.target.value)}
                      className="border-0"
                      style={{
                        background: 'rgba(255, 255, 255, 0.9)',
                        backdropFilter: 'blur(10px)',
                        border: '1px solid rgba(0, 212, 255, 0.2) !important',
                        borderRadius: '8px 0 0 8px'
                      }}
                    />
                    <InputGroup.Text 
                      className="border-0"
                      style={{
                        background: 'rgba(255, 255, 255, 0.9)',
                        border: '1px solid rgba(0, 212, 255, 0.2) !important',
                        borderLeft: 'none !important',
                        borderRadius: '0 8px 8px 0'
                      }}
                    >
                      {isLoadingCustomers ? (
                        <Spinner size="sm" />
                      ) : (
                        <FontAwesomeIcon icon={faUser} className="text-muted" />
                      )}
                    </InputGroup.Text>
                  </InputGroup>

                  {/* Customer Dropdown */}
                  {showCustomerDropdown && customerOptions.length > 0 && (
                    <div 
                      className="position-absolute w-100 mt-1"
                      style={{ zIndex: 999 }}
                    >
                      <Card className="border-0 shadow-lg">
                        <Card.Body className="p-0" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                          {customerOptions.map((customer) => (
                            <div
                              key={customer.id}
                              className="p-3 border-bottom cursor-pointer"
                              style={{ cursor: 'pointer' }}
                              onClick={() => handleCustomerSelect(customer)}
                              onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(0, 212, 255, 0.1)'}
                              onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
                            >
                              <div className="fw-semibold">{customer.first_name} {customer.last_name}</div>
                              {customer.email && <small className="text-muted">{customer.email}</small>}
                            </div>
                          ))}
                        </Card.Body>
                      </Card>
                    </div>
                  )}

                  {selectedCustomer && (
                    <div className="mt-2">
                      <div 
                        className="d-inline-flex align-items-center px-3 py-2 rounded-pill"
                        style={{
                          background: 'rgba(34, 197, 94, 0.1)',
                          border: '1px solid rgba(34, 197, 94, 0.3)'
                        }}
                      >
                        <FontAwesomeIcon icon={faUser} className="text-success me-2" />
                        <span className="me-2">{selectedCustomer.first_name} {selectedCustomer.last_name}</span>
                        <Button
                          variant="link"
                          size="sm"
                          className="p-0 text-danger"
                          onClick={() => {
                            setSelectedCustomer(null);
                            setCustomerSearchTerm('');
                            setValue("customerName", '');
                          }}
                        >
                          <FontAwesomeIcon icon={faTimes} />
                        </Button>
                      </div>
                    </div>
                  )}
                </Form.Group>
              </Col>
            </Row>

            {/* Continue Button */}
            <Row>
              <Col className="text-center">
                <Button 
                  type="submit"
                  size="lg"
                  disabled={loading || addressValue.length < 10}
                  style={{
                    background: (propertyConfirmed || addressValue.length >= 10)
                      ? 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)' 
                      : 'linear-gradient(135deg, #94a3b8 0%, #64748b 100%)',
                    border: 'none',
                    borderRadius: '12px',
                    padding: '15px 40px',
                    fontSize: '1.1rem',
                    fontWeight: 600,
                    boxShadow: (propertyConfirmed || addressValue.length >= 10) ? '0 8px 25px rgba(34, 197, 94, 0.3)' : 'none',
                    transition: 'all 0.3s ease'
                  }}
                >
                  {propertyConfirmed ? (
                    <>
                      <FontAwesomeIcon icon={faCheckCircle} className="me-2" />
                      Continue to Comparable Analysis
                    </>
                  ) : addressValue.length >= 10 ? (
                    <>
                      <FontAwesomeIcon icon={faArrowRight} className="me-2" />
                      🚀 Skip to Step 2 (Testing)
                    </>
                  ) : (
                    <>
                      <FontAwesomeIcon icon={faSearch} className="me-2" />
                      Enter Address First
                    </>
                  )}
                </Button>
              </Col>
            </Row>
          </Form>
        </Card.Body>
      </Card>
    </>
  );
};

export default Step1PropertyInputStreamlined;